import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DividerGhostButton from '@wix/da-ds/pkg/Buttons/DividerGhostButton';
import { DraggableType } from '@wix/da-shared-react/pkg/types/dnd';
import DndSortableList from '@wix/da-shared-react/pkg/Dnd/DndSortableList';
import QuestionCard from './_partials/QuestionCard';
import { Question } from '../../types';
import QuestionForm from './_partials/QuestionForm';
import { useDefaultQuestions } from './useDefaultQuestions';

import s from './SecondStep.scss';

interface Props {}

const SecondStep: React.FC<Props> = () => {
  const { t } = useTranslation();
  const [showAddQuestionForm, setShowAddQuestionForm] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null
  );
  const defaultQuestions = useDefaultQuestions();
  const [questions, setQuestions] = useState<Question[]>(defaultQuestions);

  const addQuestion = useCallback((question: Question) => {
    setQuestions(prevQuestions => [question, ...prevQuestions]);
    setShowAddQuestionForm(false);
  }, []);

  const updateQuestion = useCallback((question: Question) => {
    setQuestions(prevQuestions =>
      prevQuestions.map(q => (q.id === question.id ? question : q))
    );
    setEditingQuestionId(null);
  }, []);

  const removeQuestion = useCallback((questionId: string) => {
    setQuestions(prevQuestions =>
      prevQuestions.filter(question => question.id !== questionId)
    );
  }, []);

  const moveQuestion = useCallback(
    (currentPosition: number, newPosition: number) => {
      setQuestions(prevQuestions => {
        const newQuestions = [...prevQuestions];
        const [movedQuestion] = newQuestions.splice(currentPosition, 1);
        newQuestions.splice(newPosition, 0, movedQuestion);
        return newQuestions;
      });
    },
    []
  );

  const onAddQuestionClick = useCallback(() => {
    setShowAddQuestionForm(true);
  }, []);

  const onEditQuestionClick = useCallback(
    (questionId: string) => {
      if (!showAddQuestionForm) {
        setEditingQuestionId(questionId);
      }
    },
    [showAddQuestionForm]
  );

  const isFormOpen = showAddQuestionForm || editingQuestionId !== null;

  return (
    <div className={s['root']}>
      <p className={s['subheading']}>
        {t('originalDeviationPopup.questionnaire.subheading')}
      </p>
      {showAddQuestionForm ? (
        <QuestionForm
          onSave={addQuestion}
          onDiscard={() => setShowAddQuestionForm(false)}
        />
      ) : (
        <DividerGhostButton
          onClick={onAddQuestionClick}
          size="medium"
          disabled={isFormOpen}
        >
          {t('originalDeviationPopup.questionnaire.addQuestion')}
        </DividerGhostButton>
      )}

      <div className={s['questions']}>
        <DndSortableList
          dndType={DraggableType.ORIGINAL_DEVIATION_QUESTION}
          items={questions}
          onDragOrder={(item, position, initialPosition) => {
            moveQuestion(initialPosition, position);
          }}
        >
          {(question, sortableOptions) =>
            editingQuestionId === question.id ? (
              <QuestionForm
                key={`question-${question.id}`}
                question={question}
                onSave={updateQuestion}
                onDiscard={() => setEditingQuestionId(null)}
              />
            ) : (
              <QuestionCard
                key={`question-${question.id}`}
                question={question}
                dropRef={sortableOptions.dropRef}
                moveHandleRef={sortableOptions.handleRef}
                previewRef={sortableOptions.previewRef}
                onEdit={() => onEditQuestionClick(question.id)}
                onRemove={removeQuestion}
                disableActions={isFormOpen}
              />
            )
          }
        </DndSortableList>
      </div>
    </div>
  );
};
SecondStep.displayName = 'SecondStep';

export default SecondStep;
